import { useState } from 'react';
import cx from 'classnames';
import { makeStyles } from '@mui/styles';
import { useTheme } from '@classify/react-core-components/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import useDynamicSize from '@openclassrooms/ui-web/utils/hooks/useDynamicSize';

import { createTeleporter } from 'react-teleporter';

import { MainHeader } from 'Modules/main-header';
import {
  PredictiveCRMNewFeaturePromotion,
  PredictiveCRMPopUp,
} from 'Modules/predictive-crm/components';

import { MAIN_HEADER_HEIGHT, MAIN_HEADER_HEIGHT_SMALL } from 'Modules/main-header/constants';
import Footer from 'Modules/footer/components/Footer';
import DisclaimersTeleporter from '../../teleporters/DisclaimersTeleporter';
import { LEFT_MENU_WIDTH } from '../../constants';

const useStyles = makeStyles(
  ({ spacing, breakpoints, palette }) => {
    const paddingTopValue = spacing(2);
    return {
      menu: {
        display: 'none',
      },
      mainHeaderContainer: {
        position: 'fixed',
        width: '100%',
        zIndex: 2,
      },
      container: {
        display: 'flex',
        minHeight: '100vh',
      },
      content: {
        position: 'relative',
        zIndex: 0,
        flexGrow: 1,
        backgroundColor: palette.background.primary,
        maxWidth: '100vw',
        width: '-webkit-fill-available',
      },
      fullWidth: {
        width: '100%',
      },
      isList: {
        backgroundColor: palette.background.default,
      },
      innerContent: {
        background: ['transparent', '!important'],
        boxSizing: 'border-box',
        maxWidth: 1520,
      },
      innerContentWithSpacing: {
        paddingLeft: spacing(2),
        paddingRight: spacing(2),
        [breakpoints.up('md')]: {
          paddingLeft: spacing(4),
          paddingRight: spacing(4),
        },
        marginLeft: 'auto',
        marginRight: 'auto',
        boxSizing: 'content-box',
      },
      innerContentNoSpacing: {
        width: '100%',
        padding: 0,
        margin: [[0, 'auto']],
      },
      footerContainer: {
        marginTop: spacing(4),
        backgroundColor: palette.background.paper,
        borderTop: `1px solid ${palette.divider}`,
      },
      mainSearch: {
        position: 'relative',
        top: 64,
      },

      [breakpoints.up('md')]: {
        innerContentNoSpacing: {
          width: '100%',
        },
        content: {
          marginLeft: LEFT_MENU_WIDTH + 1,
        },
        menu: {
          background: palette.background.paper,
          paddingTop: paddingTopValue,
          width: LEFT_MENU_WIDTH,
          display: 'block',
          borderRight: `1px solid ${palette.divider}`,
          position: 'fixed',
          overflow: 'auto',
        },
      },
      [`@media (min-width:${breakpoints.values.md + LEFT_MENU_WIDTH}px)`]: {
        innerContentNoSpacing: {
          width: '100%',
        },
      },
      [`@media (min-width:${breakpoints.values.lg + LEFT_MENU_WIDTH}px)`]: {
        innerContentNoSpacing: {
          maxWidth: 1520,
        },
      },
    };
  },
  { classNamePrefix: 'FullWidthMenuScreen' }
);

export const AdditionalMenu = createTeleporter();

export const AdditionalFloatingContent = createTeleporter();

export const SubheaderContent = createTeleporter();

function FullWidthMenuScreen({
  children,
  isLegacy = false,
  variant = 'default',
  hasRouter = false,
  withSideSpacing = false,
  hideFooter = false,
  hideMenu = false,
  hideHeaderNavigation = false,
}) {
  const theme = useTheme();
  const [headerElement, setHeaderElement] = useState(null);
  const { refHeight } = useDynamicSize(headerElement);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const MainHeaderMenuPlaceholder = isMobile ? <AdditionalMenu.Target /> : null;
  const LeftMenuPlaceholder = !isMobile ? <AdditionalMenu.Target /> : null;
  const headerHeight = refHeight || (isMobile ? MAIN_HEADER_HEIGHT_SMALL : MAIN_HEADER_HEIGHT);

  const classes = useStyles();
  const isList = variant === 'list';

  return (
    <>
      <div
        className={classes.mainHeaderContainer}
        ref={(node) => {
          setHeaderElement(node);
        }}
      >
        <MainHeader
          isLegacy={isLegacy}
          hasRouter={hasRouter}
          isWide
          additionalMenu={MainHeaderMenuPlaceholder}
          isNavigationHidden={hideHeaderNavigation}
        />
      </div>

      <div id="mainSearch" className={classes.mainSearch} />

      {/* this extra container is useful for the search */}
      <div data-search-hide-element>
        <div
          className={cx(classes.container, {
            [classes.isList]: isList,
          })}
        >
          {!hideMenu && (
            <div
              className={classes.menu}
              style={{ top: headerHeight, height: `calc(100vh - ${headerHeight}px)` }}
            >
              {LeftMenuPlaceholder}
            </div>
          )}

          <div
            className={cx({
              [classes.fullWidth]: hideMenu,
              [classes.content]: !hideMenu,
              [classes.isList]: isList,
            })}
            style={{ paddingTop: headerHeight }}
          >
            <SubheaderContent.Target />
            <main
              className={cx(classes.innerContent, {
                [classes.innerContentNoSpacing]: !withSideSpacing,
                [classes.innerContentWithSpacing]: withSideSpacing,
              })}
              id="mainContent"
            >
              <DisclaimersTeleporter.Target />
              {children}
            </main>

            {!hideFooter && (
              <div className={classes.footerContainer}>
                <div className={cx(classes.innerContent, classes.innerContentWithSpacing)}>
                  <Footer />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <AdditionalFloatingContent.Target />
      <PredictiveCRMNewFeaturePromotion />
      <PredictiveCRMPopUp />
    </>
  );
}

FullWidthMenuScreen.AdditionalMenu = AdditionalMenu;
FullWidthMenuScreen.AdditionalFloatingContent = AdditionalFloatingContent;
FullWidthMenuScreen.SubheaderContent = SubheaderContent;

export default FullWidthMenuScreen;
